/**
 * ThesisExpiryStrip.tsx — MER Algo Footprints v9.2.0 — Phase 3 commit 3.5
 *
 * Renders the AI thesis expiry contract at the top of the AI Thesis panel.
 * Addresses beta tester complaint: "two-hour vigil — picked one setup, worked,
 * but two hours of continuous checking was exhausting."
 *
 * Behaviors:
 *   - Shows time relative ("in 32m") AND absolute ("4:45 PM ET")
 *   - Live ticks every 30s so relative time stays accurate without per-render churn
 *   - When expired: red EXPIRED badge + "Expired 3m ago"; parent panel can dim
 *     itself based on a CSS class hook (data-thesis-expired) we set on the
 *     wrapper if needed in a future iteration. For now we just signal visibly.
 *   - Invalidation conditions collapsible (click to expand bullet list)
 *   - When AI omitted thesisExpiry: synthesizes per-horizon fallback default
 *     (scalp +15m, intraday +30m, swing +1d, position +5d), labeled with a
 *     small ⓘ icon and amber tone.
 *
 * Auto-refresh on expiry: deferred to a follow-up commit. This component is
 * display-only — it does not mutate the orchestrator or trigger AI runs.
 */

import React, { useState, useEffect, useMemo } from 'react';
import type { TraderHorizon } from '../../types';
import '../../styles/thesisExpiryStrip.css';

// ── Per-horizon fallback durations (ms) ──────────────────────────────────────

const HORIZON_FALLBACK_MS: Record<TraderHorizon, number> = {
  scalp:    15 * 60 * 1000,                    // 15 minutes
  intraday: 30 * 60 * 1000,                    // 30 minutes
  swing:    24 * 60 * 60 * 1000,               //  1 day
  position:  5 * 24 * 60 * 60 * 1000,          //  5 days
};

// ────────────────────────────────────────────────────────────────────────────

interface Props {
  thesisExpiry?:   { expiresAt: string; invalidationConditions: string[] };
  traderHorizon:   TraderHorizon;
  // The thesis was generated at this time; fallback expiry is computed from
  // (generatedAt + HORIZON_FALLBACK_MS[horizon]). When generatedAt is missing
  // we fall back to "now" — slightly imprecise but never throws.
  generatedAt?:    number;
}

interface ResolvedExpiry {
  expiresAtMs:             number;
  invalidationConditions:  string[];
  isFallback:              boolean;
}

function resolveExpiry(props: Props): ResolvedExpiry | null {
  // (1) AI provided explicit expiry — validate it before trusting it.
  //
  // Phase 3 commit 3.5.1: the AI was occasionally returning expiresAt values
  // from its training data (e.g. dates in 2024) instead of computing now+duration.
  // Symptom: strip showed "EXPIRED 493d ago" on freshly-generated theses.
  //
  // Defensive bounds: expiresAt must be in the future, and within a sensible
  // upper bound (longer of: 4× the horizon's fallback duration, or 30 days).
  // Anything outside this range is treated as if AI omitted the field —
  // strip falls back to the synthesized per-horizon default.
  if (props.thesisExpiry?.expiresAt) {
    const ms = Date.parse(props.thesisExpiry.expiresAt);
    if (Number.isFinite(ms)) {
      const now             = Date.now();
      const horizonMs       = HORIZON_FALLBACK_MS[props.traderHorizon];
      const sensibleUpperMs = Math.max(horizonMs * 4, 30 * 24 * 60 * 60 * 1000);
      const inFuture        = ms > now;
      const notTooFar       = ms < now + sensibleUpperMs;

      if (inFuture && notTooFar) {
        return {
          expiresAtMs:            ms,
          invalidationConditions: props.thesisExpiry.invalidationConditions || [],
          isFallback:             false,
        };
      }
      // else: fall through to synthesized default below.
    }
  }

  // (2) AI omitted, returned unparseable, or returned implausible value —
  //     synthesize per-horizon default. Anchored to thesis generation time
  //     when available, otherwise "now".
  const baseMs = props.generatedAt && Number.isFinite(props.generatedAt)
    ? props.generatedAt
    : Date.now();

  return {
    expiresAtMs:            baseMs + HORIZON_FALLBACK_MS[props.traderHorizon],
    invalidationConditions: props.thesisExpiry?.invalidationConditions || [],
    isFallback:             true,
  };
}

// ── Format helpers ───────────────────────────────────────────────────────────

function formatRelative(deltaMs: number): string {
  // Returns e.g. "in 32m" / "in 2h 15m" / "3m ago" / "1d 4h ago"
  const past   = deltaMs < 0;
  const absSec = Math.abs(deltaMs) / 1000;

  if (absSec < 60) {
    return past ? 'just now' : 'in <1m';
  }

  const totalMin = Math.floor(absSec / 60);
  const days     = Math.floor(totalMin / (60 * 24));
  const hours    = Math.floor((totalMin % (60 * 24)) / 60);
  const mins     = totalMin % 60;

  let text: string;
  if (days > 0)       text = `${days}d ${hours}h`;
  else if (hours > 0) text = `${hours}h ${mins}m`;
  else                text = `${mins}m`;

  return past ? `${text} ago` : `in ${text}`;
}

function formatAbsolute(ms: number): string {
  // "4:45 PM ET" — use the user's locale time but force ET context label.
  // We don't actually convert to ET here (that requires timezone library);
  // instead we use the user's local time and label it. Acceptable for v1.
  const d  = new Date(ms);
  return d.toLocaleTimeString([], {
    hour:   'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// ────────────────────────────────────────────────────────────────────────────

export function ThesisExpiryStrip(props: Props) {
  const resolved = useMemo(() => resolveExpiry(props), [
    props.thesisExpiry, props.traderHorizon, props.generatedAt,
  ]);

  // Tick every 30s so relative-time text stays current without re-rendering on
  // every store change.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  // tick is intentionally referenced just for re-render trigger
  void tick;

  const [expanded, setExpanded] = useState(false);

  if (!resolved) return null;

  const now      = Date.now();
  const deltaMs  = resolved.expiresAtMs - now;
  const expired  = deltaMs < 0;

  const relText  = formatRelative(deltaMs);
  const absText  = formatAbsolute(resolved.expiresAtMs);

  const conditions = resolved.invalidationConditions;
  const hasConditions = conditions.length > 0;

  return (
    <div className={`tes-strip${expired ? ' tes-strip--expired' : ''}${resolved.isFallback ? ' tes-strip--fallback' : ''}`}>
      <div className="tes-row">
        <span className="tes-icon" aria-hidden>
          {expired ? '⚠' : '⏱'}
        </span>

        {expired ? (
          <span className="tes-text">
            <span className="tes-badge">EXPIRED</span>
            {' '}was set to {absText} · {relText}
          </span>
        ) : (
          <span className="tes-text">
            Expires {relText} <span className="tes-abs">({absText})</span>
          </span>
        )}

        {resolved.isFallback && !expired && (
          <span
            className="tes-fallback-marker"
            title={`AI did not specify an expiry; using ${props.traderHorizon} horizon default.`}
          >
            ⓘ default
          </span>
        )}

        {hasConditions && (
          <button
            type="button"
            className="tes-toggle"
            onClick={() => setExpanded(e => !e)}
            title={expanded ? 'Hide invalidation conditions' : 'Show invalidation conditions'}
          >
            {expanded ? '▼' : '▶'} {conditions.length} invalidation condition{conditions.length === 1 ? '' : 's'}
          </button>
        )}
      </div>

      {hasConditions && expanded && (
        <ul className="tes-conditions">
          {conditions.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
